import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { platformAuth } from "./utils/platformAuth";

export default function PlatformLogin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const handleLogin = async () => {
    if (!password) return;
    setLoading(true);
    setError("");
    const ok = await platformAuth.login(password);
    if (ok) {
      navigate("/platform", { replace: true });
      return;
    }
    setError("密码错误，请重试");
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-950 flex">
      {/* Left Visual */}
      <div className="flex-1 hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-950 to-black"></div>
          {/* Grid lines */}
          <div className="absolute inset-0 opacity-5"
            style={{ backgroundImage: "linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }}
          ></div>
          {/* Glow */}
          <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-amber-600/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-500 rounded-2xl flex items-center justify-center">
              <i className="ri-apps-2-line text-white text-lg"></i>
            </div>
            <span className="text-white text-xl font-bold tracking-tight">FanHub</span>
          </div>
          <p className="text-gray-500 text-sm">球迷协会 SaaS 平台</p>
        </div>

        <div className="relative z-10">
          <h2 className="text-white text-4xl font-bold leading-tight mb-4">
            统一管理<br />
            <span className="text-amber-400">全平台租户</span>
          </h2>
          <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
            在这里集中管理所有球迷协会租户的开通、套餐、数据监控和平台运营，实现高效的多租户 SaaS 管理。
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { value: "6", label: "租户总数" },
              { value: "6,969", label: "平台会员" },
              { value: "4", label: "运营中" },
            ].map((s) => (
              <div key={s.label} className="bg-gray-800/50 rounded-2xl p-4 border border-gray-700/50">
                <p className="text-2xl font-bold text-amber-400">{s.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-gray-700">
          FanHub Platform · 多租户运营管理系统
        </div>
      </div>

      {/* Right Login */}
      <div className="w-full lg:w-[420px] flex-shrink-0 flex items-center justify-center p-8 bg-gray-900">
        <div className="w-full max-w-sm">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-9 h-9 bg-amber-500 rounded-2xl flex items-center justify-center">
              <i className="ri-apps-2-line text-white"></i>
            </div>
            <span className="text-white text-lg font-bold">FanHub</span>
          </div>

          <h1 className="text-white text-2xl font-bold mb-1">平台运营端</h1>
          <p className="text-gray-500 text-sm mb-8">仅限授权运营人员登录</p>

          <div className="space-y-4">
            {/* Account (display only) */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">运营账号</label>
              <div className="flex items-center gap-3 bg-gray-800 rounded-xl px-4 py-3 border border-gray-700">
                <div className="w-7 h-7 bg-amber-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-star-line text-amber-400 text-sm"></i>
                </div>
                <span className="text-gray-300 text-sm">ops@fanhub.com</span>
                <span className="ml-auto text-xs text-amber-400/70 bg-amber-400/10 px-2 py-0.5 rounded-full">超管</span>
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">运营密码</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                  placeholder="请输入运营密码"
                  className={`w-full bg-gray-800 border rounded-xl px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none transition-colors pr-12 ${
                    error ? "border-red-500" : "border-gray-700 focus:border-amber-500"
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 cursor-pointer w-8 h-8 flex items-center justify-center"
                >
                  <i className={showPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
                </button>
              </div>
              {error && (
                <p className="text-xs text-red-400 mt-1.5 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i> {error}
                </p>
              )}
            </div>

            {/* Login Button */}
            <button
              onClick={handleLogin}
              disabled={!password || loading}
              className="w-full bg-amber-500 hover:bg-amber-400 text-white font-semibold py-3 rounded-xl transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm whitespace-nowrap mt-2"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i> 验证中...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-shield-keyhole-line"></i> 进入平台运营端
                </span>
              )}
            </button>
          </div>

          <div className="mt-6 pt-5 border-t border-gray-800">
            <div className="flex items-start gap-2 text-xs text-gray-600">
              <i className="ri-lock-2-line mt-0.5 flex-shrink-0"></i>
              <span>此入口为平台运营专用，协会管理员请访问各自的协会后台 <code className="text-gray-500">/admin</code></span>
            </div>
          </div>

          <div className="mt-4 text-center">
            <a href="/admin" className="text-xs text-gray-600 hover:text-gray-400 transition-colors cursor-pointer">
              前往协会管理后台 →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
